import { DASH_META_ROOT, getAuthDashMetaCsrfToken } from '@revelio/auth';
import type { ClarifyingQuestion, QuestionAnswer } from '@revelio/core';
import type { TalentDiscoveryAiFilterSearchResponseV2 } from '@revelio/data-access';

import type { LLMTabContentForApi } from '../CustomDash/components/AIAssistant/convert-tab-content-to-llm';
import {
  applyLlmTabContentToTabDispatch,
  type LLMTabContentForDispatch,
  PRIMARY_FILTER_BY_AI_ENTITY,
} from '../CustomDash/components/AIAssistant/llm-tab-content-dispatch';
import type { TabContentAction } from '../CustomDash/components/PageConfigProvider/page-reducer';
import type { GlobalAssistantConversationTurn } from './global-ai-assistant-api';

type GlobalAssistantConversationPayload = {
  turns: GlobalAssistantConversationTurn[];
  summary?: string;
};

export type DashboardPageStreamEvent =
  | { type: 'thinking'; content: string }
  | { type: 'status'; content: string }
  | { type: 'intent'; content: unknown }
  | { type: 'config'; content: LLMTabContentForDispatch }
  | { type: 'filters'; content: TalentDiscoveryAiFilterSearchResponseV2 }
  | { type: 'error'; content: string }
  | {
      type: 'questions';
      content: { questions: ClarifyingQuestion[]; triggerReason: string };
    };

async function consumeSse(
  response: Response,
  onLine: (line: string) => void,
  signal: AbortSignal | undefined
): Promise<void> {
  if (!response.body) {
    throw new Error('No response body');
  }
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  try {
    while (!signal?.aborted) {
      const { done, value } = await reader.read();
      if (done) {
        break;
      }
      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() ?? '';
      for (const line of lines) {
        onLine(line);
      }
    }
  } finally {
    reader.releaseLock();
  }
}

export async function runDashboardPageStreamCreate(params: {
  prompt: string;
  entityType: 'company' | 'geography' | 'role';
  model?: string;
  answers?: QuestionAnswer[];
  rolesTax?: string;
  skillsTax?: number;
  conversation?: GlobalAssistantConversationPayload;
  dispatch: (action: TabContentAction) => void;
  applyAiSearchFilters: (
    response: TalentDiscoveryAiFilterSearchResponseV2,
    options?: {
      primarySelectionMergeMode?: 'merge' | 'replace';
      skipTalentDiscoveryAdaptFallback?: boolean;
    }
  ) => Promise<unknown>;
  onEvent?: (event: DashboardPageStreamEvent) => void;
  signal?: AbortSignal;
}): Promise<void> {
  const {
    prompt,
    entityType,
    model,
    answers,
    rolesTax,
    skillsTax,
    conversation,
    dispatch,
    applyAiSearchFilters,
    onEvent,
    signal,
  } = params;

  const csrfToken = getAuthDashMetaCsrfToken();
  const taxonomyPayload = {
    ...(rolesTax && { rolesTax }),
    ...(skillsTax !== undefined && skillsTax !== 0 && { skillsTax }),
  };

  const response = await fetch(
    `${DASH_META_ROOT}/api/build-a-dash/ai-gen/page-stream`,
    {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'x-surf-token-dash-meta': csrfToken ?? '',
      },
      body: JSON.stringify({
        prompt,
        entityType,
        ...(model && { model }),
        ...(answers && answers.length > 0 && { answers }),
        ...taxonomyPayload,
        ...(conversation &&
        (conversation.turns.length > 0 || conversation.summary)
          ? { conversation }
          : {}),
      }),
      signal,
    }
  );

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(errorText || `HTTP ${response.status}`);
  }

  const primaryFilterId = PRIMARY_FILTER_BY_AI_ENTITY[entityType];

  const pendingFiltersRef: {
    current: TalentDiscoveryAiFilterSearchResponseV2 | undefined;
  } = { current: undefined };

  await consumeSse(
    response,
    (line) => {
      if (!line.startsWith('data: ')) {
        return;
      }
      const jsonStr = line.slice(6);
      if (!jsonStr.trim()) {
        return;
      }
      try {
        const event = JSON.parse(jsonStr) as DashboardPageStreamEvent;
        onEvent?.(event);
        if (event.type === 'config') {
          applyLlmTabContentToTabDispatch(
            dispatch,
            event.content,
            primaryFilterId
          );
        }
        if (event.type === 'filters') {
          pendingFiltersRef.current = event.content;
        }
      } catch {
        // ignore malformed chunks
      }
    },
    signal
  );

  const pendingFilters = pendingFiltersRef.current;
  if (pendingFilters === undefined) {
    return;
  }

  await new Promise<void>((resolve) => {
    setTimeout(() => {
      resolve();
    }, 0);
  });

  await applyAiSearchFilters(pendingFilters, {
    primarySelectionMergeMode: 'replace',
    skipTalentDiscoveryAdaptFallback: true,
  });
}

/** Streamed update of the current tab from natural language (Custom Dash). */
export async function runDashboardPageStreamUpdate(params: {
  newPrompt: string;
  entityType: 'company' | 'geography' | 'role';
  currentConfig: LLMTabContentForApi;
  model?: string;
  answers?: QuestionAnswer[];
  rolesTax?: string;
  skillsTax?: number;
  conversation?: GlobalAssistantConversationPayload;
  dispatch: (action: TabContentAction) => void;
  applyAiSearchFilters: (
    response: TalentDiscoveryAiFilterSearchResponseV2,
    options?: {
      primarySelectionMergeMode?: 'merge' | 'replace';
      skipTalentDiscoveryAdaptFallback?: boolean;
    }
  ) => Promise<unknown>;
  onEvent?: (event: DashboardPageStreamEvent) => void;
  signal?: AbortSignal;
}): Promise<void> {
  const {
    newPrompt,
    entityType,
    currentConfig,
    model,
    answers,
    rolesTax,
    skillsTax,
    conversation,
    dispatch,
    applyAiSearchFilters,
    onEvent,
    signal,
  } = params;

  const csrfToken = getAuthDashMetaCsrfToken();
  const taxonomyPayload = {
    ...(rolesTax && { rolesTax }),
    ...(skillsTax !== undefined && skillsTax !== 0 && { skillsTax }),
  };

  const response = await fetch(
    `${DASH_META_ROOT}/api/build-a-dash/ai-gen/page-update-from-config-stream`,
    {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'x-surf-token-dash-meta': csrfToken ?? '',
      },
      body: JSON.stringify({
        newPrompt,
        currentConfig,
        entityType,
        ...(model && { model }),
        ...(answers && answers.length > 0 && { answers }),
        ...taxonomyPayload,
        ...(conversation &&
        (conversation.turns.length > 0 || conversation.summary)
          ? { conversation }
          : {}),
      }),
      signal,
    }
  );

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(errorText || `HTTP ${response.status}`);
  }

  const primaryFilterId = PRIMARY_FILTER_BY_AI_ENTITY[entityType];

  const pendingFiltersRef: {
    current: TalentDiscoveryAiFilterSearchResponseV2 | undefined;
  } = { current: undefined };

  await consumeSse(
    response,
    (line) => {
      if (!line.startsWith('data: ')) {
        return;
      }
      const jsonStr = line.slice(6);
      if (!jsonStr.trim()) {
        return;
      }
      try {
        const event = JSON.parse(jsonStr) as DashboardPageStreamEvent;
        onEvent?.(event);
        if (event.type === 'config') {
          applyLlmTabContentToTabDispatch(
            dispatch,
            event.content,
            primaryFilterId
          );
        }
        if (event.type === 'filters') {
          pendingFiltersRef.current = event.content;
        }
      } catch {
        // ignore malformed chunks
      }
    },
    signal
  );

  const pendingFilters = pendingFiltersRef.current;
  if (pendingFilters === undefined) {
    return;
  }

  await new Promise<void>((resolve) => {
    setTimeout(() => {
      resolve();
    }, 0);
  });

  await applyAiSearchFilters(pendingFilters, {
    primarySelectionMergeMode: 'replace',
    skipTalentDiscoveryAdaptFallback: true,
  });
}
